import { useState } from 'react';
import api from '../lib/api';
import { ChatCircleText, X, PaperPlaneRight, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';

export default function AIChatWidget({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Aaniin! I'm your Pow Wow Guide. Ask me about dance categories, etiquette, finding events near you, or planning a trip." },
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages((m) => [...m, { role: 'user', text: userMsg }]);
    setInput('');
    setBusy(true);
    try {
      const r = await api.post('/chat', { message: userMsg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Sorry, I had trouble connecting. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-7rem))] bg-card border border-border rounded-2xl shadow-2xl flex flex-col fade-up" data-testid="ai-chat-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-forest text-bone rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="duotone" className="text-sand" />
          <div>
            <p className="font-display text-lg leading-none">Pow Wow Guide</p>
            <p className="text-[10px] uppercase tracking-widest text-bone/60">Pow Wow Guide</p>
          </div>
        </div>
        <button onClick={onClose} className="hover:text-sand" data-testid="ai-chat-close">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              m.role === 'user' ? 'bg-forest text-bone rounded-br-md' : 'bg-muted text-ink rounded-bl-md'
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-muted px-4 py-2.5 rounded-2xl text-sm">
              <span className="inline-flex gap-1">
                <span className="w-2 h-2 bg-terracotta rounded-full animate-bounce" />
                <span className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{animationDelay:'.1s'}} />
                <span className="w-2 h-2 bg-terracotta rounded-full animate-bounce" style={{animationDelay:'.2s'}} />
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-2 bg-muted rounded-full pl-4 pr-1.5 py-1.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask about dances, etiquette, travel..."
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid="ai-chat-input"
          />
          <Button
            onClick={send}
            disabled={busy || !input.trim()}
            size="sm"
            className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-9 w-9 p-0"
            data-testid="ai-chat-send"
          >
            <PaperPlaneRight size={16} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AIChatLauncher({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-5 py-4 shadow-2xl flex items-center gap-2 hover:scale-105 transition-transform"
      data-testid="ai-chat-launcher"
    >
      <ChatCircleText size={22} weight="duotone" />
      <span className="hidden sm:inline font-medium">Ask Guide</span>
    </button>
  );
}
